<template>
  <div class="canvas-container" :class="{'navbar-visible': isNavBarVisible, 'sidepanel-open': isSidePanelOpen}">
    <canvas ref="heroLightpass"></canvas>
    <div v-if="!firstFrameLoaded" class="spinner"></div>
    <div v-if="isShowingCompressed" class="loading-text">
      Loading high-res images...
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted, watch, nextTick } from "vue";

export default {
  name: "ScrollImageAnimation",
  props: {
    isNavBarVisible: {
      type: Boolean,
      default: false,
    },
    isSidePanelOpen: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    // Debug: Log when navbar or side panel visibility changes
    console.log('ScrollerAnimation4 - isNavBarVisible:', props.isNavBarVisible, 'isSidePanelOpen:', props.isSidePanelOpen);
    const heroLightpass = ref(null);
    let context;
    const TOTAL_FRAMES = 240;
    const LAST_FRAME_INDEX = TOTAL_FRAMES - 1; // 0-based indexing, so last frame is 299
    let isScrolling;
    let targetFrameIndex = 0;
    let currentFrameIndex = 0;
    let animating = false;
    const fps = 24;
    const frameDuration = 1000 / fps;
    let lastFrameTime = 0;
    let preloadedImages = [];
    let loadedFrames = 0;
    const firstFrameLoaded = ref(false);
    const isShowingCompressed = ref(false);
    const compressedImages = [];
    const fileNameStartsWithZero = ref(false);
    const isCurrentlyCompressed = ref(true);

    const currentFrame = (index, isCompressed) => {
      // Files start from 0.webp, so use index directly
      return isCompressed
        ? `./scroller-animation/NTM video1_1280_c10/${index}.webp`
        : `./scroller-animation/NTM video1_1280_c80/${index}.webp`;
    };

    const preloadImages = async () => {
      // First load all compressed images
      const loadCompressedBatch = async () => {
        const batchSize = 10;
        for (let i = 0; i <= LAST_FRAME_INDEX; i += batchSize) {
          const batch = [];
          for (let j = 0; j < batchSize && i + j <= LAST_FRAME_INDEX; j++) {
            const index = i + j;
            batch.push(loadCompressedImage(index));
          }
          await Promise.all(batch);
        }
      };

      // Then load high-res images
      const loadHighResBatch = async () => {
        const batchSize = 5; // Smaller batch size for high-res
        for (let i = 1; i <= LAST_FRAME_INDEX; i += batchSize) {
          const batch = [];
          for (let j = 0; j < batchSize && i + j <= LAST_FRAME_INDEX; j++) {
            const index = i + j;
            batch.push(loadHighResImage(index));
          }
          await Promise.all(batch);
        }
      };

      // Split loading functions
      const loadCompressedImage = (index) => {
        return new Promise((resolve) => {
          // Skip loading the first low-res frame (0.webp)
          if (index === 0) {
            resolve();
            return;
          }
          const compressedImg = new Image();
          compressedImg.src = currentFrame(index, true);

          compressedImg.onload = () => {
            if (!firstFrameLoaded.value && index === 0) {
              updateImage(compressedImg);
              firstFrameLoaded.value = true;
            }
            compressedImages[index] = compressedImg;
            loadedFrames++;
            resolve();
          };

          // Add error handling for compressed images
          compressedImg.onerror = () => {
            console.warn(`Failed to load compressed image ${index}`);
            // Create a blank canvas as fallback
            const fallbackCanvas = document.createElement("canvas");
            fallbackCanvas.width = 1028;
            fallbackCanvas.height = 578;
            const ctx = fallbackCanvas.getContext("2d");
            ctx.fillStyle = "#000000";
            ctx.fillRect(0, 0, fallbackCanvas.width, fallbackCanvas.height);

            // Convert canvas to image
            const fallbackImg = new Image();
            fallbackImg.src = fallbackCanvas.toDataURL();
            compressedImages[index] = fallbackImg;
            loadedFrames++;
            resolve();
          };
        });
      };

      const loadHighResImage = (index) => {
        return new Promise((resolve) => {
          const fullResImg = new Image();
          fullResImg.src = currentFrame(index, false);

          // Add timeout for high-res images
          const timeout = setTimeout(() => {
            console.warn(`Timeout loading high-res image ${index}`);
            preloadedImages[index] = compressedImages[index]; // Keep compressed version
            resolve();
          }, 10000); // 10 second timeout

          fullResImg.onload = () => {
            clearTimeout(timeout);
            preloadedImages[index] = fullResImg;
            if (!firstFrameLoaded.value && index === 0) {
              firstFrameLoaded.value = true;
            }
            if (Math.round(currentFrameIndex) === index) {
              // We are drawing a high-res image
              isCurrentlyCompressed.value = false;
              updateImage(fullResImg);
            }
            resolve();
          };

          fullResImg.onerror = () => {
            clearTimeout(timeout);
            console.warn(`Failed to load high-res image ${index}`);
            preloadedImages[index] = compressedImages[index]; // Fallback to compressed
            resolve();
          };
        });
      };

      // Load compressed images first, then high-res
      // Ensure first frame uses hi-res immediately
      await loadHighResImage(0);
      await loadCompressedBatch();
      loadHighResBatch(); // Don't await this - let it load in background
    };

    const updateImage = (img) => {
      if (img && img.complete && context) {
        context.clearRect(0, 0, context.canvas.width, context.canvas.height);
        
        // SIMPLE RULE: Math.max scaling to ALWAYS cover full area, NEVER blank space
        // This maintains aspect ratio and ensures no blank areas EVER
        const scaleX = context.canvas.width / img.width;
        const scaleY = context.canvas.height / img.height;
        const scale = Math.max(scaleX, scaleY); // ALWAYS use the LARGER scale
        
        // Apply the same scale to both dimensions (maintains aspect ratio)
        const scaledWidth = img.width * scale;
        const scaledHeight = img.height * scale;
        
        // Center the image (parts may exceed canvas bounds - that's OK!)
        const x = (context.canvas.width - scaledWidth) / 2;
        const y = (context.canvas.height - scaledHeight) / 2;
        
        console.log('SIMPLE FIX - Canvas:', context.canvas.width, 'x', context.canvas.height,
                   'Image:', img.width, 'x', img.height, 'Scale:', scale,
                   'Final:', scaledWidth, 'x', scaledHeight);
        
        context.drawImage(img, x, y, scaledWidth, scaledHeight);

        // Update compressed status based on the current state
        isShowingCompressed.value = isCurrentlyCompressed.value;
      }
    };

    const easeInOutSine = (t) => (1 + Math.sin(Math.PI * t - Math.PI / 2)) / 2;

    const animateToFrame = (timestamp) => {
      if (!animating) return;

      if (timestamp - lastFrameTime < frameDuration) {
        requestAnimationFrame(animateToFrame);
        return;
      }

      lastFrameTime = timestamp;

      const frameDifference = targetFrameIndex - currentFrameIndex;
      // Increase the animation speed for larger differences
      const speed = Math.min(1, Math.abs(frameDifference) / 10);
      
      if (Math.abs(frameDifference) < 0.01) {
        currentFrameIndex = targetFrameIndex; // Snap to target
        animating = false;
      } else {
        currentFrameIndex += frameDifference * Math.max(0.2, speed);
      }

      let roundedCurrentFrameIndex = Math.round(currentFrameIndex);

      // Modified this part to use compressed images if high-res isn't available
      if (roundedCurrentFrameIndex <= loadedFrames) {
        const pre = preloadedImages[roundedCurrentFrameIndex];
        const comp = compressedImages[roundedCurrentFrameIndex];
        const hasHighRes = !!pre && pre !== comp;
        const imageToShow = hasHighRes ? pre : comp;
        if (imageToShow) {
          isCurrentlyCompressed.value = !hasHighRes;
          updateImage(imageToShow);
        }
      } else {
        // If the target frame isn't loaded yet, show the closest available frame
        const closestFrame = Math.min(roundedCurrentFrameIndex, loadedFrames);
        const pre = preloadedImages[closestFrame];
        const comp = compressedImages[closestFrame];
        const hasHighRes = !!pre && pre !== comp;
        const fallbackImage = hasHighRes ? pre : comp;
        if (fallbackImage) {
          isCurrentlyCompressed.value = !hasHighRes;
          updateImage(fallbackImage);
        }
      }

      requestAnimationFrame(animateToFrame);
    };

    const handleScroll = () => {
      clearTimeout(isScrolling);
      const scrollTop = document.documentElement.scrollTop;
      const maxScrollTop = document.documentElement.scrollHeight - window.innerHeight;
      const scrollFraction = scrollTop / maxScrollTop;
      
      // Ensure scrollFraction is clamped between 0 and 1
      const clampedScrollFraction = Math.max(0, Math.min(1, scrollFraction));
      
      // Adjusted to start from 0
      targetFrameIndex = Math.floor(
        Math.max(0, Math.min(LAST_FRAME_INDEX, 
        easeInOutSine(clampedScrollFraction) * LAST_FRAME_INDEX))
      );

      console.log({
        scrollTop,
        maxScrollTop,
        scrollFraction,
        clampedScrollFraction,
        targetFrameIndex
      });

      if (!animating) {
        animating = true;
        requestAnimationFrame(animateToFrame);
      }

      isScrolling = setTimeout(() => {
        animating = false;
      }, 200);
    };

    let updateCanvasSize;
    let resizeTimeout;

    // Watch for UI state changes - synchronized transitions prevent conflicts
    watch(() => props.isNavBarVisible, (newVal, oldVal) => {
      console.log('NavBar visibility changed:', oldVal, '->', newVal);
      
      // Clear any pending resize
      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
      }
      
      // IMMEDIATE update - synchronized 0.3s transitions handle smooth animation
      if (updateCanvasSize) {
        updateCanvasSize();
      }
    });

    watch(() => props.isSidePanelOpen, (newVal, oldVal) => {
      console.log('Side panel visibility changed:', oldVal, '->', newVal);
      
      // Clear any pending resize
      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
      }
      
      // IMMEDIATE canvas update - synchronized transitions handle the timing
      // Both canvas (0.3s) and side panel (0.3s) use same duration and easing
      if (updateCanvasSize) {
        updateCanvasSize();
      }
    });

    onMounted(() => {
      const canvas = heroLightpass.value;
      context = canvas.getContext("2d");
      
      // Set canvas resolution to maintain proper aspect ratio
      updateCanvasSize = () => {
        // PREDICT final display dimensions based on UI state (don't wait for DOM)
        // This prevents distortion during CSS transitions
        let predictedDisplayWidth, predictedDisplayHeight;
        
        // Calculate predicted dimensions based on current UI state
        if (props.isSidePanelOpen) {
          predictedDisplayWidth = window.innerWidth * 0.7; // 70vw when side panel open
        } else {
          predictedDisplayWidth = window.innerWidth; // 100vw when side panel closed
        }
        
        if (props.isNavBarVisible) {
          predictedDisplayHeight = window.innerHeight * 0.88; // ~88vh when navbar visible
        } else {
          predictedDisplayHeight = window.innerHeight; // 100vh when navbar hidden
        }
        
        const predictedAspectRatio = predictedDisplayWidth / predictedDisplayHeight;
        
        // Set canvas resolution based on PREDICTED final dimensions
        // This ensures smooth transitions without distortion
        canvas.width = Math.round(predictedDisplayWidth);
        canvas.height = Math.round(predictedDisplayHeight);
        
        // Get actual current display size for comparison
        const rect = canvas.getBoundingClientRect();
        
        console.log('SMOOTH TRANSITION SIZING:');
        console.log('  UI State - NavBar:', props.isNavBarVisible, 'SidePanel:', props.isSidePanelOpen);
        console.log('  Predicted:', predictedDisplayWidth, 'x', predictedDisplayHeight, 'ratio:', predictedAspectRatio.toFixed(3));
        console.log('  Actual DOM:', rect.width, 'x', rect.height, 'DOM ratio:', (rect.width/rect.height).toFixed(3));
        console.log('  Canvas resolution:', canvas.width, 'x', canvas.height);
        
        // IMPORTANT: Always redraw current frame immediately after resize
        // This ensures the image scales smoothly during the transition
        const currentIndex = Math.round(currentFrameIndex);
        const highResImage = preloadedImages[currentIndex];
        const compressedImage = compressedImages[currentIndex];
        const imageToShow = highResImage || compressedImage;
        
        if (imageToShow) {
          updateImage(imageToShow);
        }
      };
      
      updateCanvasSize();
      window.addEventListener("resize", updateCanvasSize);
      
      preloadImages();
      window.addEventListener("scroll", handleScroll);
    });

    onUnmounted(() => {
      window.removeEventListener("scroll", handleScroll);
      if (updateCanvasSize) {
        window.removeEventListener("resize", updateCanvasSize);
      }
      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
      }
    });

    return {
      heroLightpass,
      isShowingCompressed,
      firstFrameLoaded,
      fileNameStartsWithZero,
    };
  },
};
</script>

<style scoped>
html {
  height: 100%;
}

body {
  height: 1000%;
  background: #000;
}

canvas {
  position: fixed;
  top: 6vh;
  width: 100vw;
  height: 94vh;
  z-index: -11;
  /* Synchronized with side panel transition duration (0.3s ease-in-out) */
  transition: top 0.3s ease-in-out, height 0.3s ease-in-out, width 0.3s ease-in-out, left 0.3s ease-in-out;
}

.navbar-visible canvas {
  top: 12vh !important;
  height: 88vh !important;
}

.sidepanel-open canvas {
  width: 70vw !important;
}

/* Combined state: both navbar and side panel visible */
.navbar-visible.sidepanel-open canvas {
  top: 12vh !important;
  height: 88vh !important;
  width: 70vw !important;
}

.canvas-container {
  position: relative;
  width: 100%;
  height: 100%;
}

.spinner {
  position: fixed;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 50px;
  height: 50px;
  border: 5px solid #f3f3f3;
  border-top: 5px solid #3498db;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  z-index: 1;
}

.loading-text {
  position: fixed;
  left: 20px;
  bottom: 20px;
  color: white;
  background: rgba(0, 0, 0, 0.5);
  padding: 8px 12px;
  border-radius: 4px;
  z-index: 1;
}

@keyframes spin {
  0% {
    transform: translate(-50%, -50%) rotate(0deg);
  }
  100% {
    transform: translate(-50%, -50%) rotate(360deg);
  }
}
</style>